import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, ChevronUp, ChevronDown, Edit2, Trash2, Filter, RefreshCw, Upload, Download, AlertCircle, User } from 'lucide-react';
import API_BASE_URL from '../config';
import Swal from 'sweetalert2';

const Requestor = () => {
  const [requestors, setRequestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filterField, setFilterField] = useState('all');
  const [editingItem, setEditingItem] = useState(null);
  const [importData, setImportData] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    id: 0,
    name: '',
    department: '',
    signature: null // Byte array for signature (optional)
  });

  const [signaturePreview, setSignaturePreview] = useState(null);
  const signatureInputRef = useRef(null);

  const REQUESTOR_API = `${API_BASE_URL}api/Requestor`;

  // Fetch requestors from API
  const fetchRequestors = async () => {
    setLoading(true);
    try {
      const response = await fetch(REQUESTOR_API);
      if (response.ok) {
        const data = await response.json();
        setRequestors(data);
      }
    } catch (error) {
      console.error('Error fetching requestors:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch requestors!',
        confirmButtonColor: '#ef4444'
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRequestors();
  }, []);

  // Create new requestor
  const createRequestor = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(REQUESTOR_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify({
          id: 0,
          name: formData.name,
          department: formData.department,
          signature: formData.signature // Optional - can be null
        })
      });

      if (response.ok) {
        await fetchRequestors();
        closeModal();
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Requestor created successfully',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error('Failed to create requestor');
      }
    } catch (error) {
      console.error('Error creating requestor:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create requestor!',
        confirmButtonColor: '#ef4444'
      });
    }
    setLoading(false);
  };

  // Update requestor
  const updateRequestor = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${REQUESTOR_API}/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify({
          id: editingItem.id,
          name: formData.name,
          department: formData.department,
          signature: formData.signature || editingItem.signature // Keep existing if not changed
        })
      });

      if (response.ok) {
        await fetchRequestors();
        closeModal();
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Requestor updated successfully',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error('Failed to update requestor');
      }
    } catch (error) {
      console.error('Error updating requestor:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update requestor!',
        confirmButtonColor: '#ef4444'
      });
    }
    setLoading(false);
  };

  // Delete requestor
  const deleteRequestor = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Requestor?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await fetch(`${REQUESTOR_API}/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setRequestors(requestors.filter(item => item.id !== id));
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Requestor has been deleted',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error deleting requestor:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete requestor!',
        confirmButtonColor: '#ef4444'
      });
    }
    setLoading(false);
  };

  // Delete all requestors
  const deleteAllRequestors = async () => {
    if (requestors.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Requestors',
        text: 'No requestors to delete!',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete All Requestors?',
      html: `You are about to delete <strong>${requestors.length}</strong> requestors.<br/>This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete all!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    const doubleConfirm = await Swal.fire({
      title: 'Final Warning!',
      text: 'This will permanently delete all requestors. Are you absolutely sure?',
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'DELETE ALL',
      cancelButtonText: 'Cancel'
    });

    if (!doubleConfirm.isConfirmed) return;

    setLoading(true);

    let deletedCount = 0;
    const totalItems = requestors.length;

    Swal.fire({
      title: 'Deleting Requestors',
      html: `
        <div style="text-align: center;">
          <div style="font-size: 48px; font-weight: bold; color: #ef4444; margin: 20px 0;">
            ${deletedCount}/${totalItems}
          </div>
          <div style="color: #64748b;">Deleting requestors...</div>
        </div>
      `,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      let successCount = 0;
      let failCount = 0;

      for (const requestor of requestors) {
        try {
          const response = await fetch(`${REQUESTOR_API}/${requestor.id}`, {
            method: 'DELETE'
          });
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
          console.error(`Error deleting requestor ${requestor.id}:`, error);
        }

        deletedCount++;
        Swal.update({
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; font-weight: bold; color: #ef4444; margin: 20px 0;">
                ${deletedCount}/${totalItems}
              </div>
              <div style="color: #64748b;">Deleting requestors...</div>
            </div>
          `
        });
      }

      await fetchRequestors();

      Swal.fire({
        icon: successCount === totalItems ? 'success' : 'warning',
        title: 'Deletion Complete',
        html: `
          <div style="text-align: left;">
            <p>✅ Deleted: <strong>${successCount}</strong></p>
            ${failCount > 0 ? `<p>❌ Failed: <strong>${failCount}</strong></p>` : ''}
          </div>
        `,
        confirmButtonColor: '#3b82f6'
      });
    } catch (error) {
      console.error('Error deleting all requestors:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while deleting requestors!',
        confirmButtonColor: '#ef4444'
      });
    }

    setLoading(false);
  };

  // Export to CSV
  const handleExport = () => {
    if (requestors.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Data',
        text: 'No requestors to export!',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const headers = [
      'id',
      'name',
      'department'
      // Signature is excluded from export (byte array)
    ];

    const csvContent = [
      headers.join(','),
      ...requestors.map(item =>
        headers.map(header => `"${item[header] || ''}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `requestors_export_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      icon: 'success',
      title: 'Exported!',
      text: `${requestors.length} requestors exported successfully`,
      timer: 2000,
      showConfirmButton: false
    });
  };

  // Handle file import
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File',
          text: 'CSV file must contain headers and at least one row!',
          confirmButtonColor: '#ef4444'
        });
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const data = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const item = {};
        headers.forEach((header, index) => {
          item[header] = values[index] || '';
        });
        data.push(item);
      }

      checkDuplicates(data);
      setShowImportModal(true);
    };

    reader.readAsText(file);
  };

  // Check for duplicates based on name
  const checkDuplicates = (newItems) => {
    const dupes = [];
    const nonDupes = [];

    newItems.forEach(newItem => {
      const existingItem = requestors.find(item =>
        item.name?.toLowerCase() === newItem.name?.toLowerCase()
      );

      if (existingItem) {
        dupes.push({
          ...newItem,
          existingId: existingItem.id,
          existing: existingItem,
          action: 'skip'
        });
      } else {
        nonDupes.push(newItem);
      }
    });

    setImportData(nonDupes);
    setDuplicates(dupes);
  };

  // Handle duplicate action change
  const handleDuplicateAction = (index, action) => {
    const updated = [...duplicates];
    updated[index].action = action;
    setDuplicates(updated);
  };

  // Process import
  const processImport = async () => {
    setLoading(true);

    const totalItems = importData.length + duplicates.filter(d => d.action !== 'skip').length;
    let processedCount = 0;

    Swal.fire({
      title: 'Importing Requestors',
      html: `
        <div style="text-align: center;">
          <div style="font-size: 48px; font-weight: bold; color: #3b82f6; margin: 20px 0;">
            ${processedCount}/${totalItems}
          </div>
          <div style="color: #64748b;">Processing requestors...</div>
        </div>
      `,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // Process non-duplicates
      for (const item of importData) {
        await fetch(REQUESTOR_API, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'text/plain'
          },
          body: JSON.stringify({
            id: 0,
            name: item.name || '',
            department: item.department || '',
            signature: null // Signature not imported from CSV
          })
        });

        processedCount++;
        Swal.update({
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; font-weight: bold; color: #3b82f6; margin: 20px 0;">
                ${processedCount}/${totalItems}
              </div>
              <div style="color: #64748b;">Processing requestors...</div>
            </div>
          `
        });
      }

      // Process duplicates
      for (const dup of duplicates) {
        if (dup.action === 'update') {
          await fetch(`${REQUESTOR_API}/${dup.existingId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'text/plain'
            },
            body: JSON.stringify({
              id: dup.existingId,
              name: dup.name || '',
              department: dup.department || '',
              signature: dup.existing.signature || null // Keep existing signature
            })
          });
          processedCount++;
        } else if (dup.action === 'delete') {
          await fetch(`${REQUESTOR_API}/${dup.existingId}`, {
            method: 'DELETE'
          });
          processedCount++;
        }

        if (dup.action !== 'skip') {
          Swal.update({
            html: `
              <div style="text-align: center;">
                <div style="font-size: 48px; font-weight: bold; color: #3b82f6; margin: 20px 0;">
                  ${processedCount}/${totalItems}
                </div>
                <div style="color: #64748b;">Processing requestors...</div>
              </div>
            `
          });
        }
      }

      await fetchRequestors();
      closeImportModal();

      Swal.fire({
        icon: 'success',
        title: 'Import Complete!',
        text: `Successfully imported ${totalItems} requestors`,
        confirmButtonColor: '#3b82f6'
      });
    } catch (error) {
      console.error('Error importing requestors:', error);
      Swal.fire({
        icon: 'error',
        title: 'Import Failed',
        text: 'An error occurred during import',
        confirmButtonColor: '#ef4444'
      });
    }

    setLoading(false);
  };

  // Open modal for create
  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      id: 0,
      name: '',
      department: '',
      signature: null
    });
    setShowModal(true);
  };

  // Open modal for edit
  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    
    // Load signature preview if exists
    if (item.signature) {
      setSignaturePreview(`data:image/png;base64,${item.signature}`);
    } else {
      setSignaturePreview(null);
    }
    
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      id: 0,
      name: '',
      department: '',
      signature: null
    });
    setSignaturePreview(null);
    if (signatureInputRef.current) {
      signatureInputRef.current.value = '';
    }
  };

  // Close import modal
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportData([]);
    setDuplicates([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle signature image upload
  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File',
        text: 'Please upload an image file (PNG, JPG, etc.)',
        confirmButtonColor: '#ef4444'
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'File Too Large',
        text: 'Signature image must be less than 2MB',
        confirmButtonColor: '#ef4444'
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setSignaturePreview(event.target.result);
    };
    reader.readAsDataURL(file);

    // Convert to base64 for API
    const base64Reader = new FileReader();
    base64Reader.onload = (event) => {
      const base64String = event.target.result.split(',')[1]; // Remove data:image prefix
      setFormData({ ...formData, signature: base64String });
    };
    base64Reader.readAsDataURL(file);
  };

  // Remove signature
  const removeSignature = () => {
    setFormData({ ...formData, signature: null });
    setSignaturePreview(null);
    if (signatureInputRef.current) {
      signatureInputRef.current.value = '';
    }
  };

  // Filter requestors based on search term and filter field
  const filteredRequestors = requestors.filter(item => {
    const searchLower = searchTerm.toLowerCase();

    if (filterField === 'all') {
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.department?.toLowerCase().includes(searchLower)
      );
    } else {
      return item[filterField]?.toLowerCase().includes(searchLower);
    }
  });

  // Sort requestors
  const sortedRequestors = [...filteredRequestors].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedRequestors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedRequestors.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="opacity-30" size={16} />;
    return sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '2.5rem 1.5rem',
      fontFamily: '"Outfit", -apple-system, sans-serif'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .glass-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          border: none;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: 'Outfit', sans-serif;
        }

        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .btn-success:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }

        .btn-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
        }

        .btn-warning:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
        }

        .btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
        }

        .btn-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        .btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .btn-secondary {
          background: #ffffff;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .btn-secondary:hover {
          background: #f8f9fa;
          border-color: #cbd5e1;
          transform: translateY(-1px);
        }

        .input-field {
          width: 100%;
          padding: 0.875rem 1rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          color: #1e293b;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          font-family: 'Outfit', sans-serif;
        }

        .input-field:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .input-field::placeholder {
          color: #94a3b8;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 12px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #475569;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s ease;
          border-bottom: 2px solid #e2e8f0;
        }

        th:hover {
          background: #f1f5f9;
        }

        td {
          padding: 1rem;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          font-size: 0.9rem;
        }

        tr:hover {
          background: #f8f9fa;
        }

        tr:last-child td {
          border-bottom: none;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-content-large {
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-width: 900px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .pagination {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
        }

        .page-btn {
          padding: 0.5rem 0.875rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          font-family: 'Outfit', sans-serif;
          min-width: 40px;
        }

        .page-btn:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #3b82f6;
          transform: translateY(-1px);
        }

        .page-btn.active {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-color: #3b82f6;
          color: white;
        }

        .page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .spinner {
          border: 3px solid #e2e8f0;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .icon-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .icon-btn:hover {
          background: #f1f5f9;
        }

        .icon-btn.edit:hover {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .icon-btn.delete:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        select {
          padding: 0.625rem 2.5rem 0.625rem 1rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #1e293b;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Outfit', sans-serif;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.875rem center;
        }

        select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        label {
          display: block;
          margin-bottom: 0.5rem;
          color: #475569;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .badge {
          display: inline-block;
          padding: 0.375rem 0.75rem;
          background: #eff6ff;
          color: #3b82f6;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .badge-warning {
          background: #fef3c7;
          color: #d97706;
        }

        .badge-success {
          background: #d1fae5;
          color: #059669;
        }

        .badge-danger {
          background: #fee2e2;
          color: #dc2626;
        }

        .alert {
          padding: 1rem;
          border-radius: 10px;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .alert-warning {
          background: #fef3c7;
          border: 1px solid #fde68a;
          color: #92400e;
        }

        .duplicate-item {
          background: #fef3c7;
          border: 1px solid #fde68a;
          padding: 1rem;
          border-radius: 10px;
          margin-bottom: 0.75rem;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem 1rem;
          border-radius: 6px;
          border: none;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Outfit', sans-serif;
        }

        .action-btn.active {
          font-weight: 600;
        }

        .action-btn.skip {
          background: #e2e8f0;
          color: #475569;
        }

        .action-btn.skip.active {
          background: #cbd5e1;
        }

        .action-btn.update {
          background: #dbeafe;
          color: #1e40af;
        }

        .action-btn.update.active {
          background: #3b82f6;
          color: white;
        }

        .action-btn.delete {
          background: #fee2e2;
          color: #991b1b;
        }

        .action-btn.delete.active {
          background: #ef4444;
          color: white;
        }

        input[type="file"] {
          display: none;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div style={{ maxWidth: '1500px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem',
            letterSpacing: '-0.02em',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <User size={40} style={{ color: '#3b82f6' }} />
            Requestor Maintenance
          </h1>
          <p style={{ color: '#64748b', fontSize: '1.05rem' }}>
            Manage requestor information and profiles
          </p>
        </div>

        {/* Controls */}
        <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8'
                }}
              />
              <input
                type="text"
                placeholder="Search requestors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
                style={{ paddingLeft: '2.75rem' }}
              />
            </div>

            {/* Filter */}
            <div style={{ position: 'relative' }}>
              <Filter
                size={18}
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8',
                  pointerEvents: 'none'
                }}
              />
              <select
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
                style={{ paddingLeft: '2.75rem' }}
              >
                <option value="all">All Fields</option>
                <option value="name">Name</option>
                <option value="department">Department</option>
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchRequestors}
              className="btn btn-secondary"
              disabled={loading}
              style={{ justifySelf: 'start' }}
            >
              <RefreshCw size={18} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>

            {/* Import/Export Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', justifySelf: 'end', flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-success"
              >
                <Upload size={18} />
                Import
              </button>
              <button
                onClick={handleExport}
                className="btn btn-warning"
              >
                <Download size={18} />
                Export
              </button>
              <button
                onClick={deleteAllRequestors}
                className="btn btn-danger"
                disabled={loading || requestors.length === 0}
              >
                <Trash2 size={18} />
                Delete All
              </button>
              <button
                onClick={openCreateModal}
                className="btn btn-primary"
              >
                <Plus size={18} />
                Add New
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #f1f5f9',
            flexWrap: 'wrap'
          }}>
            <div className="badge">
              Total Requestors: {requestors.length}
            </div>
            <div className="badge">
              Filtered: {filteredRequestors.length}
            </div>
            <div className="badge">
              Page {currentPage} of {totalPages || 1}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="glass-card" style={{ marginBottom: '1.5rem' }}>
          <div className="table-container">
            {loading && currentItems.length === 0 ? (
              <div style={{
                padding: '4rem',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div className="spinner"></div>
                <p style={{ color: '#64748b' }}>Loading requestors...</p>
              </div>
            ) : currentItems.length === 0 ? (
              <div style={{
                padding: '4rem',
                textAlign: 'center'
              }}>
                <User size={64} style={{ color: '#cbd5e1', margin: '0 auto 1rem' }} />
                <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
                  {searchTerm ? 'No requestors found matching your search.' : 'No requestors yet. Click "Add New" to get started.'}
                </p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('id')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        ID
                        <SortIcon field="id" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('name')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Name
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('department')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Department
                        <SortIcon field="department" />
                      </div>
                    </th>
                    <th style={{ textAlign: 'center' }}>Signature</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: '600', color: '#3b82f6' }}>#{item.id}</td>
                      <td style={{ fontWeight: '500' }}>{item.name}</td>
                      <td>{item.department}</td>
                      <td style={{ textAlign: 'center' }}>
                        {item.signature ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            <img 
                              src={`data:image/png;base64,${item.signature}`} 
                              alt="Signature"
                              style={{ 
                                maxWidth: '80px', 
                                maxHeight: '40px', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                padding: '2px'
                              }}
                            />
                            <span className="badge-success" style={{ fontSize: '0.75rem' }}>✓</span>
                          </div>
                        ) : (
                          <span className="badge">No Signature</span>
                        )}
                      </td>
                      <td>
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          justifyContent: 'center'
                        }}>
                          <button
                            onClick={() => openEditModal(item)}
                            className="icon-btn edit"
                            title="Edit"
                          >
                            <Edit2 size={18} color="#64748b" />
                          </button>
                          <button
                            onClick={() => deleteRequestor(item.id)}
                            className="icon-btn delete"
                            title="Delete"
                          >
                            <Trash2 size={18} color="#64748b" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="glass-card" style={{ padding: '1rem' }}>
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>

              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`page-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber} style={{ color: '#94a3b8' }}>...</span>;
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Last
              </button>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content glass-card" onClick={(e) => e.stopPropagation()}>
              <div style={{
                padding: '2rem',
                borderBottom: '1px solid #f1f5f9'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    fontSize: '1.75rem',
                    fontWeight: '600',
                    color: '#1e293b'
                  }}>
                    {editingItem ? 'Edit Requestor' : 'Add New Requestor'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="icon-btn"
                    style={{ padding: '0.5rem' }}
                  >
                    <X size={24} color="#64748b" />
                  </button>
                </div>
              </div>

              <form onSubmit={editingItem ? updateRequestor : createRequestor}>
                <div style={{ padding: '2rem' }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="name">Name *</label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      placeholder="Enter full name"
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="department">Department *</label>
                    <input
                      id="department"
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      placeholder="Enter department"
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="signature">Signature Image (Optional)</label>
                    
                    {signaturePreview ? (
                      <div style={{ 
                        border: '2px dashed #e2e8f0', 
                        borderRadius: '10px', 
                        padding: '1.5rem',
                        textAlign: 'center',
                        background: '#f8f9fa'
                      }}>
                        <img 
                          src={signaturePreview} 
                          alt="Signature Preview" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '150px',
                            marginBottom: '1rem',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            background: 'white',
                            padding: '0.5rem'
                          }} 
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            type="button"
                            onClick={() => signatureInputRef.current?.click()}
                            className="btn btn-secondary"
                            style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                          >
                            Change Image
                          </button>
                          <button
                            type="button"
                            onClick={removeSignature}
                            className="btn btn-danger"
                            style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                          >
                            <X size={16} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        onClick={() => signatureInputRef.current?.click()}
                        style={{ 
                          border: '2px dashed #cbd5e1', 
                          borderRadius: '10px', 
                          padding: '2rem',
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8f9fa',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.background = '#eff6ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.background = '#f8f9fa';
                        }}
                      >
                        <Upload size={40} style={{ color: '#94a3b8', margin: '0 auto 0.75rem' }} />
                        <p style={{ color: '#64748b', fontWeight: '500', marginBottom: '0.25rem' }}>
                          Click to upload signature image
                        </p>
                        <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                          PNG, JPG up to 2MB
                        </p>
                      </div>
                    )}
                    
                    <input
                      ref={signatureInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureUpload}
                      style={{ display: 'none' }}
                    />
                    
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
                      * Signature image is optional and can be left empty
                    </p>
                  </div>
                </div>

                <div style={{
                  padding: '1.5rem 2rem',
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                        {editingItem ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingItem ? 'Update Requestor' : 'Create Requestor'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="modal-overlay" onClick={closeImportModal}>
            <div className="modal-content-large glass-card" onClick={(e) => e.stopPropagation()}>
              <div style={{
                padding: '2rem',
                borderBottom: '1px solid #f1f5f9'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    fontSize: '1.75rem',
                    fontWeight: '600',
                    color: '#1e293b'
                  }}>
                    Import Requestors
                  </h2>
                  <button
                    onClick={closeImportModal}
                    className="icon-btn"
                    style={{ padding: '0.5rem' }}
                  >
                    <X size={24} color="#64748b" />
                  </button>
                </div>
              </div>

              <div style={{ padding: '2rem' }}>
                {/* Summary */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap'
                }}>
                  <div className="badge-success">
                    New Requestors: {importData.length}
                  </div>
                  <div className="badge-warning">
                    Duplicates: {duplicates.length}
                  </div>
                </div>

                {/* Duplicates Section */}
                {duplicates.length > 0 && (
                  <div>
                    <div className="alert alert-warning">
                      <AlertCircle size={20} />
                      <div>
                        <strong>Duplicate Requestors Found!</strong>
                        <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                          The following requestors have matching names. Choose an action for each:
                        </p>
                      </div>
                    </div>

                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {duplicates.map((dup, index) => (
                        <div key={index} className="duplicate-item">
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '1rem',
                            marginBottom: '0.75rem'
                          }}>
                            <div>
                              <strong style={{ color: '#92400e' }}>Existing Requestor:</strong>
                              <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                                <div>Name: {dup.existing.name}</div>
                                <div>Department: {dup.existing.department}</div>
                              </div>
                            </div>
                            <div>
                              <strong style={{ color: '#92400e' }}>Import Requestor:</strong>
                              <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                                <div>Name: {dup.name}</div>
                                <div>Department: {dup.department}</div>
                              </div>
                            </div>
                          </div>

                          <div className="action-buttons">
                            <button
                              className={`action-btn skip ${dup.action === 'skip' ? 'active' : ''}`}
                              onClick={() => handleDuplicateAction(index, 'skip')}
                            >
                              Skip
                            </button>
                            <button
                              className={`action-btn update ${dup.action === 'update' ? 'active' : ''}`}
                              onClick={() => handleDuplicateAction(index, 'update')}
                            >
                              Update Existing
                            </button>
                            <button
                              className={`action-btn delete ${dup.action === 'delete' ? 'active' : ''}`}
                              onClick={() => handleDuplicateAction(index, 'delete')}
                            >
                              Delete Existing
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Items Preview */}
                {importData.length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h3 style={{
                      color: '#1e293b',
                      fontSize: '1.1rem',
                      marginBottom: '0.75rem'
                    }}>
                      New Requestors to Import ({importData.length})
                    </h3>
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      background: '#f8f9fa',
                      padding: '1rem',
                      borderRadius: '8px'
                    }}>
                      {importData.slice(0, 5).map((item, index) => (
                        <div key={index} style={{
                          fontSize: '0.9rem',
                          padding: '0.5rem 0',
                          borderBottom: index < 4 ? '1px solid #e2e8f0' : 'none'
                        }}>
                          {item.name} - {item.department}
                        </div>
                      ))}
                      {importData.length > 5 && (
                        <div style={{
                          fontSize: '0.9rem',
                          color: '#64748b',
                          marginTop: '0.5rem'
                        }}>
                          ... and {importData.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={closeImportModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={processImport}
                  className="btn btn-primary"
                  disabled={loading || (importData.length === 0 && duplicates.length === 0)}
                >
                  {loading ? (
                    <>
                      <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                      Processing...
                    </>
                  ) : (
                    'Import Requestors'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Requestor;